import React, { useState, useEffect, useRef } from 'react'
import { createPortal } from 'react-dom'
import { generateFormula } from './editorApi'
import { executeFormula } from './formulaUtils'
import './FormulaModal.css'

function highlightFormula (formula) {
  if (!formula) return ''
  return formula.replace(/\b(value|num)\b/g, '<span style="color:#e68619;font-weight:600">$1</span>')
}

function highlightTemplate (template) {
  if (!template) return ''
  return template
    .replace(/\b(value)\b/g, '<span style="color:#e68619;font-weight:600">$1</span>')
    .replace(/\b(result)\b/g, '<span style="color:#7b1fa2;font-weight:600">$1</span>')
}

function autoResizeTextarea (textarea) {
  if (!textarea) return
  const overlay = textarea.nextElementSibling
  textarea.style.height = 'auto'
  if (overlay) overlay.style.height = 'auto'
  textarea.offsetHeight // force reflow
  const newHeight = Math.max(40, Math.min(160, textarea.scrollHeight))
  textarea.style.height = newHeight + 'px'
  if (overlay) {
    overlay.style.height = (newHeight - 2) + 'px'
    overlay.scrollTop = textarea.scrollTop
    overlay.scrollLeft = textarea.scrollLeft
  }
}

export function FormulaModal ({ panel, isOpen, onClose, onSave }) {
  const [template, setTemplate] = useState('')
  const [formula, setFormula] = useState('')
  const [selectionValue, setSelectionValue] = useState('')
  const [aiPrompt, setAiPrompt] = useState('')
  const [aiSuggestion, setAiSuggestion] = useState(null)
  const [aiLoading, setAiLoading] = useState(false)
  const [showAiAssistant, setShowAiAssistant] = useState(false)
  const modalRef = useRef(null)
  const formulaRef = useRef(null)
  const templateRef = useRef(null)

  useEffect(() => {
    if (panel && isOpen) {
      if (panel.formula) {
        try {
          const parsed = JSON.parse(panel.formula)
          setTemplate(parsed.template || '')
          setFormula(parsed.formula || '')
        } catch (_) {
          setFormula(panel.formula)
          setTemplate('')
        }
      } else {
        setTemplate('')
        setFormula('')
      }
      setSelectionValue(panel.directText || panel.text || '')
      setAiSuggestion(null)
      setAiPrompt('')
      setTimeout(() => {
        if (formulaRef.current) autoResizeTextarea(formulaRef.current)
        if (templateRef.current) autoResizeTextarea(templateRef.current)
      }, 0)
    }
  }, [panel, isOpen])

  // Close on outside click
  useEffect(() => {
    if (!isOpen) return
    function handleOutside (e) {
      if (modalRef.current && !modalRef.current.contains(e.target)) {
        onClose()
      }
    }
    document.addEventListener('mousedown', handleOutside)
    return () => document.removeEventListener('mousedown', handleOutside)
  }, [isOpen, onClose])

  // Close on Escape
  useEffect(() => {
    if (!isOpen) return
    function handleKey (e) {
      if (e.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', handleKey)
    return () => document.removeEventListener('keydown', handleKey)
  }, [isOpen, onClose])

  async function handleAiGenerate () {
    if (!aiPrompt.trim()) return
    setAiLoading(true)
    try {
      const result = await generateFormula(aiPrompt.trim(), selectionValue || undefined)
      setAiSuggestion({
        formula: result.formula || 'value',
        template: result.output_template || 'result'
      })
    } catch (err) {
      console.error('AI formula generation error:', err)
      setAiSuggestion({ formula: 'value', template: 'result' })
    } finally {
      setAiLoading(false)
    }
  }

  function handleApplyAiSuggestion () {
    if (!aiSuggestion) return
    setFormula(aiSuggestion.formula)
    setTemplate('result')
    setTimeout(() => {
      if (formulaRef.current) autoResizeTextarea(formulaRef.current)
      if (templateRef.current) autoResizeTextarea(templateRef.current)
    }, 0)
  }

  function handleSave () {
    onSave(template, formula)
    onClose()
  }

  const preview = executeFormula(selectionValue, template, formula)

  if (!isOpen || !panel) return null

  if (typeof document === 'undefined') return null

  return createPortal(
    <div className='formula-modal-portal'>
      <div className='formula-modal-backdrop' role='presentation'>
        <div className='formula-modal' ref={modalRef} role='dialog' aria-modal='true' aria-labelledby='formula-modal-title'>
          <div className='formula-modal-header'>
            <span id='formula-modal-title' className='formula-modal-title'>Formula Editor</span>
            <button type='button' className='formula-modal-close' onClick={onClose} title='Close'>✕</button>
          </div>

          <div className='formula-modal-content'>
          {/* Input value */}
          <div className='formula-modal-section'>
            <label className='formula-modal-label formula-modal-label--input'>Input Value:</label>
            <div className='formula-modal-input-display'>{selectionValue || 'No value'}</div>
          </div>

          {/* AI assistant */}
          <div className='formula-modal-section'>
            <div className='formula-modal-ai-section'>
              <div className='formula-modal-ai-toggle'>
                <button
                  type='button'
                  className='formula-modal-ai-toggle-btn'
                  onClick={() => setShowAiAssistant(!showAiAssistant)}
                >
                  <span className='formula-modal-ai-toggle-icon'>{showAiAssistant ? '▼' : '▶'}</span>
                  <span className='formula-modal-ai-toggle-text'>🤖 AI Formula Assistant (Optional)</span>
                </button>
              </div>

              {showAiAssistant && (
                <div className='formula-modal-ai-content'>
                  <div className='formula-modal-input-container'>
                    <textarea
                      className='formula-modal-ai-input'
                      placeholder='Describe what you want to do with this data...'
                      value={aiPrompt}
                      onChange={(e) => setAiPrompt(e.target.value)}
                    />
                  </div>
                  <div className='formula-modal-ai-buttons'>
                    <button
                      className='formula-modal-btn formula-modal-btn--primary'
                      onClick={handleAiGenerate}
                      disabled={!aiPrompt.trim() || aiLoading}
                    >
                      {aiLoading ? 'Generating…' : 'Generate'}
                    </button>
                    {aiSuggestion && (
                      <button
                        className='formula-modal-btn formula-modal-btn--secondary'
                        onClick={handleApplyAiSuggestion}
                      >
                        Apply
                      </button>
                    )}
                  </div>
                  {aiSuggestion && (
                    <div className='formula-modal-ai-suggestion'>
                      <div className='formula-modal-ai-suggestion-header'>💡 Suggestion:</div>
                      <div className='formula-modal-ai-suggestion-content'>
                        <div><strong>Formula:</strong><code>{aiSuggestion.formula}</code></div>
                        <div><strong>Template:</strong><code>{aiSuggestion.template}</code></div>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Formula input */}
          <div className='formula-modal-section'>
            <label className='formula-modal-label formula-modal-label--result'>Result:</label>
            <div className='formula-modal-input-container'>
              <textarea
                ref={formulaRef}
                className='formula-modal-input'
                placeholder="e.g., upper(value) or num > 20 ? 'Hot' : 'Cold'"
                value={formula}
                onChange={(e) => { setFormula(e.target.value); autoResizeTextarea(e.target) }}
                onScroll={(e) => {
                  const overlay = e.target.nextElementSibling
                  if (overlay) { overlay.scrollTop = e.target.scrollTop; overlay.scrollLeft = e.target.scrollLeft }
                }}
              />
              <div
                className='formula-modal-overlay-highlight'
                dangerouslySetInnerHTML={{ __html: highlightFormula(formula) || '' }}
              />
            </div>
            <div className='formula-modal-help'>
              Use <span className='formula-modal-help-variable formula-modal-help-variable--input'>value</span> (text),{' '}
              <span className='formula-modal-help-variable formula-modal-help-variable--input'>num</span> (number).
              Supports Math.*, upper(), lower(), and ternary conditions (? :).
            </div>
          </div>

          {/* Template input */}
          <div className='formula-modal-section'>
            <label className='formula-modal-label formula-modal-label--neutral'>Final Output:</label>
            <div className='formula-modal-input-container'>
              <textarea
                ref={templateRef}
                className='formula-modal-input'
                placeholder='e.g., The temperature is value which equals result°F'
                value={template}
                onChange={(e) => { setTemplate(e.target.value); autoResizeTextarea(e.target) }}
                onScroll={(e) => {
                  const overlay = e.target.nextElementSibling
                  if (overlay) { overlay.scrollTop = e.target.scrollTop; overlay.scrollLeft = e.target.scrollLeft }
                }}
              />
              <div
                className='formula-modal-overlay-highlight'
                dangerouslySetInnerHTML={{ __html: highlightTemplate(template) || '' }}
              />
            </div>
            <div className='formula-modal-help'>
              Use <span className='formula-modal-help-variable formula-modal-help-variable--input'>value</span> for original text,{' '}
              <span className='formula-modal-help-variable formula-modal-help-variable--result'>result</span> for calculated result.
            </div>
          </div>

          {/* Preview */}
          <div className='formula-modal-section'>
            <label className='formula-modal-label formula-modal-label--neutral'>Preview:</label>
            <div className='formula-modal-preview'>{preview}</div>
          </div>
          </div>

          <div className='formula-modal-buttons'>
            <button type='button' className='formula-modal-btn formula-modal-btn--cancel' onClick={onClose}>
              <span className='formula-modal-btn-icon' aria-hidden>✕</span>
              Cancel
            </button>
            <button type='button' className='formula-modal-btn formula-modal-btn--save' onClick={handleSave}>
              <span className='formula-modal-btn-icon' aria-hidden>✓</span>
              Save Formula
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  )
}
